import React from 'react';
import { track } from '@vercel/analytics';
import { useTheme } from '../context/ThemeContext';

// Shared ad component. Two layouts:
//   variant="grid"   - matches the article grid card on the Landing page
//   variant="banner" - full-width inline banner used inside article content
//
// Both variants render a 16:9 image, the ad title, and a clear "Annonse"
// tab-mark pinned to the top-left so the sponsored nature is unambiguous.
function AdCard({ ad, variant = 'grid' }) {
  const { darkMode } = useTheme();
  if (!ad) return null;

  // Ads can be created without a linkUrl — in that case the card renders as
  // a non-clickable display. Drop hover treatments so it doesn't pretend to
  // be interactive.
  const hasLink = !!ad.linkUrl;
  const hoverBorder = hasLink ? 'hover:border-amber-500/60' : '';
  const hoverShadow = hasLink ? 'hover:shadow-md' : '';
  const cardBorder = darkMode
    ? `bg-slate-900 border-slate-800 ${hoverBorder}`
    : `bg-white border-stone-200 ${hoverBorder} shadow-sm ${hoverShadow}`;
  const titleColor = darkMode
    ? `text-white ${hasLink ? 'group-hover:text-amber-300' : ''}`
    : `text-stone-900 ${hasLink ? 'group-hover:text-amber-700' : ''}`;
  const metaColor = darkMode ? 'text-gray-500' : 'text-stone-400';

  // Inline "Annonse" pill rendered in the content area (replaces the previous
  // overlay on top of the image). Shaped like the article tag pills so the
  // card sits cleanly in the grid.
  const annonsePill = (
    <span
      className="inline-block px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider rounded-full bg-amber-400 text-slate-900"
      style={{ fontFamily: "'Lora', serif" }}
    >
      Annonse
    </span>
  );

  // Host-only label (e.g. "bilnerden.no") shown in the date slot so the ad
  // has the same vertical rhythm as a normal article card. Empty when the
  // ad has no link.
  let hostname = '';
  if (ad.linkUrl) {
    try {
      hostname = new URL(ad.linkUrl).hostname.replace(/^www\./, '');
    } catch {
      hostname = '';
    }
  }

  const handleClick = () => {
    track('ad_click', {
      adId: ad.id,
      title: ad.title,
      host: hostname,
      variant,
    });
  };

  if (variant === 'banner') {
    const containerBg = darkMode
      ? `bg-slate-900 border-slate-800 ${hasLink ? 'hover:border-amber-500/60' : ''}`
      : `bg-white border-stone-200 shadow-sm ${hasLink ? 'hover:border-amber-500/60 hover:shadow-md' : ''}`;
    const inner = (
      <div className={`relative overflow-hidden rounded-2xl border transition-all duration-150 ${containerBg}`}>
        <div className="aspect-[16/9] overflow-hidden">
          <img
            src={ad.imageUrl}
            alt={ad.title}
            className={`w-full h-full object-cover transition-transform duration-200 ${hasLink ? 'group-hover:scale-[1.02]' : ''}`}
            loading="lazy"
          />
        </div>
        <div className="p-4 sm:p-5">
          <div className="mb-2">{annonsePill}</div>
          <div
            className={`text-base sm:text-lg font-bold leading-snug transition-colors ${titleColor}`}
            style={{ fontFamily: "'Lora', serif" }}
          >
            {ad.title}
          </div>
        </div>
      </div>
    );
    if (!hasLink) {
      return (
        <div className="block my-10 not-prose" aria-label={`Annonse: ${ad.title}`}>
          {inner}
        </div>
      );
    }
    return (
      <a
        href={ad.linkUrl}
        target="_blank"
        rel="noopener sponsored noreferrer"
        onClick={handleClick}
        className="group block my-10 not-prose"
        aria-label={`Annonse: ${ad.title}`}
      >
        {inner}
      </a>
    );
  }

  // grid variant — matches the article card layout one-for-one: image, tag
  // row, title (2-line clamp), excerpt slot (hidden sm:block), host line.
  // The "Annonse" pill lives under the image in the tag row. `h-full` on
  // the wrapper is important: when it sits in a CSS grid row whose height
  // is driven by a taller sibling, we want the ad card to stretch to match
  // instead of leaving the grid cell half-filled.
  const gridInner = (
    <article className={`h-full rounded-xl overflow-hidden border transition-all duration-150 ${cardBorder}`}>
      <div className="aspect-video overflow-hidden">
        <img
          src={ad.imageUrl}
          alt={ad.title}
          className={`w-full h-full object-cover transition-transform duration-200 ${hasLink ? 'group-hover:scale-[1.03]' : ''}`}
          loading="lazy"
        />
      </div>
      <div className="p-3 sm:p-5">
        {/* Tag row — desktop only, mirroring the article card which hides
            its tags with `hidden sm:flex`. Keeps the card the same height
            as a regular article card on mobile. */}
        <div className="hidden sm:flex flex-wrap gap-1.5 mb-2">
          {annonsePill}
        </div>
        <h3
          className={`font-bold text-sm sm:text-base mb-1 sm:mb-2 transition-colors duration-150 line-clamp-2 leading-snug ${titleColor}`}
          style={{ fontFamily: "'Lora', serif" }}
        >
          {ad.title}
        </h3>
        <p className={`text-xs sm:text-sm leading-relaxed line-clamp-2 mb-2 sm:mb-3 hidden sm:block ${metaColor}`} style={{ fontFamily: "'Lora', serif" }}>
          {hostname
            ? `Sponset innhold fra ${hostname}.`
            : 'Sponset innhold.'}
        </p>
        {/* Meta row. On mobile we prefix with a compact "Annonse" chip so
            the disclosure is still visible even though the big pill row is
            hidden; on desktop we show the hostname alone (the big pill
            above already discloses the sponsorship). */}
        <div className={`text-xs flex items-center gap-1.5 ${metaColor}`}>
          <span className="sm:hidden inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-400 text-slate-900">
            Annonse
          </span>
          {hostname && <span className="truncate">{hostname}</span>}
        </div>
      </div>
    </article>
  );

  if (!hasLink) {
    return (
      <div className="block h-full" aria-label={`Annonse: ${ad.title}`}>
        {gridInner}
      </div>
    );
  }

  return (
    <a
      href={ad.linkUrl}
      target="_blank"
      rel="noopener sponsored noreferrer"
      onClick={handleClick}
      className="group block h-full"
      aria-label={`Annonse: ${ad.title}`}
    >
      {gridInner}
    </a>
  );
}

export default AdCard;
